<script setup lang="ts">
// Corner-claim voice-confirmation prompt (round-27, 2026-08-31) — mounted once in App.vue, like
// ToastStack, so it's visible regardless of which screen is current. Renders only while
// services/confirmFlow.ts's confirmState.status === 'awaiting'.
//
// Requirement: "voice must never be the only way out" — the Sí/No buttons are always live,
// full tap-targets, independent of whether the mic ever hears an answer at all. A countdown bar
// shows the ~10s deadline (confirmFlow.ts's DECISION_TIMEOUT_MS) ticking down; a timeout
// resolves itself (confirmFlow.ts's own setTimeout) and this component just stops rendering.
import { computed, ref, watch } from 'vue'
import Avatar from './Avatar.vue'
import { confirmState, resolveConfirmation } from '../services/confirmFlow'

const isEs = computed(() => confirmState.lang !== 'en')
const question = computed(() => {
  const name = confirmState.candidate?.displayName ?? ''
  return isEs.value ? `¿Eres ${name}?` : `Are you ${name}?`
})
const sideLabel = computed(() => {
  if (!confirmState.color) return ''
  if (isEs.value) return confirmState.color === 'red' ? 'campo rojo' : 'campo azul'
  return confirmState.color === 'red' ? 'red side' : 'blue side'
})

// Countdown bar: a CSS transition from 100% -> 0% width, timed to the exact remaining ms at
// mount — restarted (via the :key on the outer element) every time a NEW confirmation starts
// (deadlineTs changes), so a replaced confirmation's bar doesn't visually jump.
const barEl = ref<HTMLElement | null>(null)
watch(
  () => confirmState.deadlineTs,
  (deadline) => {
    if (deadline == null || !barEl.value) return
    const remainingMs = Math.max(0, deadline - Date.now())
    const el = barEl.value
    el.style.transition = 'none'
    el.style.width = '100%'
    // Force a reflow so the transition below actually animates from 100%, not from whatever the
    // previous confirmation's bar was mid-animation to.
    void el.offsetWidth
    requestAnimationFrame(() => {
      el.style.transition = `width ${remainingMs}ms linear`
      el.style.width = '0%'
    })
  },
  { immediate: true },
)
</script>

<template>
  <div v-if="confirmState.status === 'awaiting'" class="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 px-4">
    <div class="w-full max-w-sm rounded-2xl bg-neutral-900 border border-neutral-700 shadow-2xl overflow-hidden">
      <div class="h-1.5 bg-neutral-800">
        <div ref="barEl" class="h-full bg-amber-500" style="width: 100%" />
      </div>
      <div class="p-5 flex flex-col items-center gap-3 text-center">
        <Avatar :player="confirmState.candidate" :size="72" />
        <div>
          <p class="text-xl font-bold">{{ question }}</p>
          <p class="text-xs text-neutral-400 mt-1">
            {{ isEs ? `Voz reconocida al ${sideLabel}` : `Voice heard on the ${sideLabel}` }}
            <span v-if="confirmState.confidencePct != null"> · {{ confirmState.confidencePct }}%</span>
          </p>
        </div>
        <div class="flex gap-3 w-full mt-2">
          <button
            type="button"
            class="tap-target flex-1 rounded-xl bg-neutral-800 active:bg-neutral-700 text-lg font-bold"
            @click="resolveConfirmation('no', 'tap')"
          >
            {{ isEs ? 'No' : 'No' }}
          </button>
          <button
            type="button"
            class="tap-target flex-1 rounded-xl bg-emerald-600 active:bg-emerald-500 text-lg font-bold"
            @click="resolveConfirmation('yes', 'tap')"
          >
            {{ isEs ? 'Sí' : 'Yes' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
